import { chatsService } from '../services/chats.service';

const getActiveChatRoute = () => {
  if (typeof window === 'undefined') return { chatId: null, chatType: null };

  const matches = window.location.pathname.match(/^\/messages\/([^/]+)\/([^/?#]+)/);

  return {
    chatType: matches?.[1] ?? null,
    chatId: matches?.[2] ?? null,
  };
};

const isActiveChatRoute = (chatId, chatType) => {
  const activeRoute = getActiveChatRoute();

  return activeRoute.chatId === chatId && activeRoute.chatType === chatType;
};

const chatsModel = {
  state: {
    chatsFilters: {
      searchText: '',
    },
    isLoadingChats: true,
    isComposerLocked: false,
    selectedChat: null,
    isFetchedChatById: true,
    chats: [],
    error: false,
  },
  reducers: {
    setData(state, payload) {
      return { ...state, ...payload };
    },
    setSearchTextFilterValue(state, searchText) {
      return {
        ...state,
        chatsFilters: {
          ...state.chatsFilters,
          searchText,
        },
      };
    },
    updateReactionLastActivityAndMoveChatToTop(state, payload) {
      const { chatId, messageId, reaction, reactedBy, messageType, contentPreview, lastActivityAt } = payload;

      const existing = state.chats.find(c => c._id === chatId);

      if (!existing) return state;

      const updatedChat = {
        ...existing,
        lastActivity: {
          type: 'reaction',
          reaction: {
            messageId,
            reaction,
            reactedBy,
            messageType,
            contentPreview,
          },
        },
        lastActivityAt,
      };

      return {
        ...state,
        chats: [updatedChat, ...state.chats.filter(c => c._id !== chatId)],
      };
    },
    updateMessageLastActivityAndMoveChatToTop(state, payload) {
      const { chatId, lastActivity, lastActivityAt } = payload;

      const existingChat = state.chats.find(chat => chat._id === chatId);

      if (!existingChat) {
        return state;
      }

      const updatedChat = {
        ...existingChat,
        lastActivity,
        lastActivityAt,
      };

      return {
        ...state,
        chats: [updatedChat, ...state.chats.filter(chat => chat._id !== chatId)],
      };
    },
    restoreLastActivityAndResort(state, payload) {
      const { chatId, restoredLastActivity } = payload;

      return {
        ...state,
        chats: state.chats.map(chat =>
          chat._id !== chatId
            ? chat
            : {
                ...chat,
                lastActivity: restoredLastActivity,
              }
        ),
      };
    },
    updateChatLastActivitySilentAndIncrementUnreadMessageCount(state, payload) {
      const { chatId, lastActivity, lastActivityAt } = payload;

      const existingChat = state.chats.find(chat => chat._id === chatId);

      if (!existingChat) {
        return state;
      }

      const updatedChat = {
        ...existingChat,
        lastActivity,
        lastActivityAt,
        unreadMessageCount: (existingChat.unreadMessageCount ?? 0) + 1,
      };

      return {
        ...state,
        chats: [updatedChat, ...state.chats.filter(chat => chat._id !== chatId)],
      };
    },
    updateChatAsRead(state, payload) {
      const { chatId } = payload;

      return {
        ...state,
        chats: state.chats.map(chat =>
          chat._id === chatId
            ? {
                ...chat,
                unreadMessageCount: 0,
              }
            : chat
        ),
      };
    },
  },
  effects: dispatch => ({
    async getChats(payload) {
      try {
        const response = await chatsService.fetchChats(payload);
        if (response.status === 200) {
          this.setData({
            chats: response?.data?.data || [],
            isLoadingChats: false,
          });

          return;
        }

        this.setData({ error: true });
      } catch (error) {
        this.setData({ error: true });
      }
    },
    async getGroupChatById(chatId) {
      try {
        if (!chatId || !isActiveChatRoute(chatId, 'group')) {
          return;
        }

        const response = await chatsService.fetchGroupChatById(chatId);

        if (!isActiveChatRoute(chatId, 'group')) {
          return;
        }

        if (response.status === 200) {
          this.setData({
            selectedChat: response?.data?.data || [],
            isFetchedChatById: false,
          });

          return;
        }

        this.setData({ error: true });
      } catch (error) {
        this.setData({ error: true });
      }
    },
    async getPairChatById(chatId) {
      try {
        if (!chatId || !isActiveChatRoute(chatId, 'pair')) {
          return;
        }

        const response = await chatsService.fetchPairChatById(chatId);

        if (!isActiveChatRoute(chatId, 'pair')) {
          return;
        }

        if (response.status === 200) {
          this.setData({
            selectedChat: response?.data?.data || [],
            isFetchedChatById: false,
          });

          return;
        }

        this.setData({ error: true });
      } catch (error) {
        this.setData({ error: true });
      }
    },
  }),
};

export default chatsModel;

export const selectChatById = (state, chatId) => {
  return state.chatsModel.chats.find(chat => chat._id === chatId);
};

export const hasChat = (state, chatId) => {
  return state.chatsModel.chats.some(chat => chat._id === chatId);
};
